import React, { useRef, useState } from 'react';
import { GripVertical, Eye, EyeOff } from 'lucide-react';
import { useIntl } from 'react-intl';
import Modal from '../common/Modal';
import type { DailySectionKey, HideableSectionKey } from '../../types/onboarding';
import './DailyLayoutSettings.scss';

interface DailyLayoutSettingsProps {
  sections: DailySectionKey[];
  hiddenSections: HideableSectionKey[];
  onSave: (sections: DailySectionKey[], hiddenSections: HideableSectionKey[]) => void;
  onClose: () => void;
}

const SECTION_LABEL_KEYS: Record<HideableSectionKey, string> = {
  habits: 'daily.settings.habits',
  notes: 'daily.settings.notes',
  tasks: 'daily.settings.tasks',
  calendar: 'daily.settings.calendar',
  focus: 'daily.settings.focus',
};

const ORDERABLE_SECTIONS: DailySectionKey[] = ['habits', 'tasks', 'notes'];
const STATIC_SECTIONS: HideableSectionKey[] = ['calendar', 'focus'];

function orderableSections(init: DailySectionKey[]): DailySectionKey[] {
  const filtered = init.filter((key): key is DailySectionKey => ORDERABLE_SECTIONS.includes(key));
  const missing = ORDERABLE_SECTIONS.filter((key) => !filtered.includes(key));
  return [...filtered, ...missing];
}

const DailyLayoutSettings: React.FC<DailyLayoutSettingsProps> = ({
  sections: initSections,
  hiddenSections: initHidden,
  onSave,
  onClose,
}) => {
  const { formatMessage: t } = useIntl();
  const [sections, setSections] = useState<DailySectionKey[]>(() => orderableSections(initSections));
  const [hiddenSet, setHiddenSet] = useState<Set<HideableSectionKey>>(
    () => new Set(initHidden),
  );
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const dragFromRef = useRef<number | null>(null);
  const dragOverRef = useRef<number | null>(null);

  const toggleVisibility = (key: HideableSectionKey) => {
    setHiddenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const moveSection = (from: number, to: number) => {
    if (from === to) return;
    setSections((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const indexFromClientY = (clientY: number) => {
    const items = listRef.current?.querySelectorAll<HTMLElement>('[data-layout-idx]');
    if (!items?.length) return null;
    const first = items[0].getBoundingClientRect();
    const last = items[items.length - 1].getBoundingClientRect();
    if (clientY < first.top) return 0;
    if (clientY > last.bottom) return items.length - 1;
    for (let i = 0; i < items.length; i += 1) {
      const rect = items[i].getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) return i;
    }
    return null;
  };

  const handlePointerDown = (idx: number) => (event: React.PointerEvent<HTMLLIElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // jsdom and some browsers do not implement pointer capture
    }
    dragFromRef.current = idx;
    dragOverRef.current = idx;
    setDragIdx(idx);
    setDragOverIdx(idx);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLLIElement>) => {
    if (dragFromRef.current === null) return;
    const over = indexFromClientY(event.clientY);
    if (over === null || over === dragOverRef.current) return;
    dragOverRef.current = over;
    setDragOverIdx(over);
  };

  const handlePointerUp = () => {
    const from = dragFromRef.current;
    const to = dragOverRef.current;
    dragFromRef.current = null;
    dragOverRef.current = null;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from !== null && to !== null) moveSection(from, to);
  };

  const handleSave = () => {
    onSave(sections, Array.from(hiddenSet));
    onClose();
  };

  return (
    <Modal
      title={t({ id: 'daily.settings.title' })}
      onClose={onClose}
      maxWidth={420}
      onSave={handleSave}
      saveLabel={t({ id: 'common.save' })}
    >
      <div className="daily-layout-settings">
        <p className="daily-layout-settings__subtitle">
          {t({ id: 'daily.settings.sections' })}
        </p>

        <ul className="daily-layout-settings__list" ref={listRef}>
          {sections.map((key, idx) => (
            <li
              key={key}
              data-layout-idx={idx}
              aria-label={t({ id: SECTION_LABEL_KEYS[key] })}
              className={[
                'daily-layout-settings__item',
                dragIdx === idx ? 'daily-layout-settings__item--dragging' : '',
                dragOverIdx === idx && dragIdx !== idx ? 'daily-layout-settings__item--drag-over' : '',
              ].join(' ')}
              onPointerDown={handlePointerDown(idx)}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <span className="daily-layout-settings__drag-handle" aria-hidden="true">
                <GripVertical size={14} />
              </span>
              <span className="daily-layout-settings__name">
                {t({ id: SECTION_LABEL_KEYS[key] })}
              </span>
              <button
                className="daily-layout-settings__toggle"
                onClick={() => toggleVisibility(key)}
                type="button"
                title={hiddenSet.has(key) ? t({ id: 'daily.settings.show' }) : t({ id: 'daily.settings.hide' })}
                aria-label={hiddenSet.has(key) ? t({ id: 'daily.settings.show' }) : t({ id: 'daily.settings.hide' })}
              >
                {hiddenSet.has(key) ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </li>
          ))}
        </ul>

        <div className="daily-layout-settings__separator" />

        {STATIC_SECTIONS.map((key) => (
          <div
            key={key}
            role="group"
            aria-label={t({ id: SECTION_LABEL_KEYS[key] })}
            className="daily-layout-settings__item daily-layout-settings__item--static"
          >
            <span className="daily-layout-settings__name">
              {t({ id: SECTION_LABEL_KEYS[key] })}
            </span>
            <button
              className="daily-layout-settings__toggle"
              onClick={() => toggleVisibility(key)}
              type="button"
              title={hiddenSet.has(key) ? t({ id: 'daily.settings.show' }) : t({ id: 'daily.settings.hide' })}
              aria-label={hiddenSet.has(key) ? t({ id: 'daily.settings.show' }) : t({ id: 'daily.settings.hide' })}
            >
              {hiddenSet.has(key) ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default DailyLayoutSettings;
